import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { CdkConnectedOverlay } from '@angular/cdk/overlay';
import {
    AfterViewInit,
    Component,
    computed,
    effect,
    ElementRef,
    EventEmitter,
    HostListener,
    inject,
    Input,
    input,
    model,
    OnDestroy,
    OnInit,
    Output,
    signal,
    viewChild,
    ViewEncapsulation,
} from '@angular/core';
import { DraggableEnpsComponent } from '@common/components/base/draggable-enps-component';
import { PSTFieldNames } from '@common/data-models/fields/pst-field-names';
import { CardViewType, FieldDefValidations } from '@common/enums/enums';
import { GlobalTablesService } from '@common/services/global-tables-service';
import { LanguageSettings } from '@common/services/language-settings';
import { VisualStateService } from '@common/services/visual-state-service';
import { Store } from '@ngrx/store';
import { DateTime } from '@src/suite/common/data-models/complex-types/date-time';
import { CARD_WIDTH_LOWER_BREAKPOINTS } from '@src/suite/common/enums/constants';
import { LayoutState } from '@src/suite/common/services/layout-state';
import { OpenFullscreenModalActions } from '@src/suite/common/store/fullscreen';
import { selectFieldDefsDict } from '@src/suite/common/store/global-tables/global-tables.selectors';
import { selectTranslatedKey } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { selectViewStateCustomCardItems, selectViewStateFilterState, selectViewStateSearchString } from '@src/suite/common/store/navigation/selectors/viewstate.selectors';
import { RecordHttpActions, StoriesRecordActions } from '@src/suite/common/store/records/records.actions';
import { i18nKeys } from '@src/suite/common/utils/i18n.default';
import { getStorySecurityPrivsWrite } from '@src/suite/common/utils/record-utils';
import { parseXml } from '@src/suite/common/utils/xml-utils';
import { AppService } from '@suite/app.service';
import { getItemString } from '@suite/common/utils/util';

@Component({
    selector: 'st-story-item',
    templateUrl: './story-item.html',
    styleUrls: ['./story-item.scss'],
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'st-story-item',
    },
})
export class StoryItemComponent extends DraggableEnpsComponent implements OnInit, AfterViewInit, OnDestroy {
    store = inject(Store);
    visualStateService = inject(VisualStateService);
    globalTablesService = inject(GlobalTablesService);
    languageSettings = inject(LanguageSettings);
    elmRef = inject(ElementRef);

    readonly FieldDefValidations = FieldDefValidations;
    readonly getItemString = getItemString;
    readonly PSTFieldNames = PSTFieldNames;

    private containerResizeObserver: ResizeObserver;
    CardViewTypes = CardViewType;
    searchString$ = this.store.select(selectViewStateSearchString);
    itemStringCache = new Map<string, string>();
    toolTipData: Array<string> = [];
    isListOpen: boolean = false;
    isAssignmentsPanelOpen = false;
    storyOwnerEl = viewChild('storyOwnerEl', { read: CdkConnectedOverlay });

    data = model<StorytellingStoryRecord>(null);
    topicRecord = input<StorytellingTopicRecord>(null);
    inView = signal(false);
    cardViewType = signal(CardViewType.LARGE);
    preview = input(false);
    disabled = input(false, {
        transform: coerceBooleanProperty,
    });
    // itemClicked = output();
    @Output() public itemClicked: EventEmitter<void> = new EventEmitter();
    scriptStoryTypeLabel$ = this.store.select(selectTranslatedKey(i18nKeys.PSTWebListValue_PSTStoryOutputType_Script));

    inFullscreenMode = input(false, {
        transform: coerceBooleanProperty,
    });

    inSidecarOpenedMode = input(false, {
        transform: coerceBooleanProperty,
    });

    isReadOnly = input(false, {
        transform: coerceBooleanProperty,
    });

    @Output() public storyDeleteEmitter: EventEmitter<number> = new EventEmitter();

    viewStateFilterState$ = this.store.select(selectViewStateFilterState);
    viewStateCustomCardItems = this.store.selectSignal(selectViewStateCustomCardItems);
    viewStateFilterState = this.store.selectSignal(selectViewStateFilterState);

    customCardItemsBam = computed(() => {
        if (this._customCardItems?.length > 0 && this.preview()) {
            return this._customCardItems;
        }
        return this.viewStateCustomCardItems();
    });

    storyText = computed(() => {
        if (this.data() && this.data().text) {
            const linkRegex = new RegExp(/<(?!\/?a|br(?:\s+[^>]*?>|\s*?>))[^>]+>/gi);
            const prodRegex = new RegExp(/\[(?:PROD|MOS)\][\s\S]*?\[\/(?:PROD|MOS)\]/gs);
            return this.data().text.replace(linkRegex, '').replace(prodRegex, '');
        }
    });

    private _customCardItems: FieldDef[];
    @Input()
    public set customCardItems(v: FieldDef[]) {
        this._customCardItems = v;
    }

    get timeUpdatedLabel() {
        return `${this.store.selectSignal(selectTranslatedKey(i18nKeys.PSTWebChangedAgoLabel))()} ${this.data()?.ModTime?.moment?.fromNow()}`;
    }

    get timeUpdatedByLabel() {
        return `${this.timeUpdatedLabel} ${this.updatedBy}`;
    }

    get updatedBy() {
        return `${this.store.selectSignal(selectTranslatedKey(i18nKeys.PSTWebChangedByLabel))()} ${this.globalTablesService.Staff[this?.data()?.ModBy]?.DisplayName}`;
    }

    addToTopicLabel = computed(() => {
        return this.store.selectSignal(selectTranslatedKey(i18nKeys.PSTWebAddToTopicLabel))();
    });

    addToRundownLabel = computed(() => {
        return this.store.selectSignal(selectTranslatedKey(i18nKeys.PSTWebAddToRundownLabel))();
    });

    get showFullCard(): boolean {
        return !(this.visualStateService.isLessThanMedium || (this.inFullscreenMode() && this.visualStateService.isLessThanXLarge));
    }

    editable = computed(() => {
        return getStorySecurityPrivsWrite(this.data()) && !this.data()?.Path?.isWasteBinPath;
    });

    cardDisabled = computed(() => {
        return this.isReadOnly() || !this.editable() || this.disabled();
    });

    displayOpenActionButtonHoverTitle = computed(() => {
        return this.globalTablesService.getDisplayOpenActionButtonHoverTitle(this.inSidecarOpenedMode())();
    });

    @HostListener('click')
    public onClick() {
        if (this.cardDisabled()) {
            return;
        }
        if (this.cardViewType() === CardViewType.SMALL) {
            this.itemClicked.emit();
        }
    }

    async ngOnInit() {
        setTimeout(() => {
            this.inView.set(true);
        }, 0);
    }

    ngAfterViewInit() {
        this.containerResizeObserver = new ResizeObserver(entries => {
            const width = entries[0].contentRect.width;
            if (width < CARD_WIDTH_LOWER_BREAKPOINTS.medium) {
                this.cardViewType.set(CardViewType.SMALL);
            } else if (width < CARD_WIDTH_LOWER_BREAKPOINTS.large) {
                this.cardViewType.set(CardViewType.MEDIUM);
            } else {
                this.cardViewType.set(CardViewType.LARGE);
            }
        });

        this.containerResizeObserver.observe(this.elmRef.nativeElement);
    }

    ngOnDestroy(): void {
        this.containerResizeObserver.unobserve(this.elmRef.nativeElement);
        this.containerResizeObserver.disconnect();
    }

    updateStory(value: any, fieldId: string) {
        if (this.cardDisabled()) return;
        const fieldDef = [{ value, key: fieldId }];
        // TODO: not cool to have this title logic here
        if (fieldId === PSTFieldNames.Title) {
            if (!value) return;

            this.data().Title = value.trim();
            this.data().Slug = value.trim();
            fieldDef.push({ value: value, key: PSTFieldNames.Slug });
        }
        if (fieldId === PSTFieldNames.PSTTags) {
            this.data().Tags = value;
        }
        this.store.dispatch(RecordHttpActions.saveRecordChanges({ record: this.data(), fieldsToUpdate: fieldDef }));
    }

    timezoneChanged(fieldId: string, event: DateTime) {
        this.data.update(val => {
            val[fieldId] = event;
            return val;
        });
        this.store.dispatch(StoriesRecordActions.updateStories({ records: [this.data()] }));
    }

    storyTypes = computed(() => {
        return this.data()?.PSTStoryOutputType;
    });

    enforceRequiredFieldsOnCreate = computed(() => {
        return this.globalTablesService.EnforceRequiredFieldsOnCreate;
    });

    maxTagsToShow = computed(() => {
        if (this.visualStateService.isLessThanLarge) {
            return 4;
        }
        if (this.visualStateService.isLessThanXLarge) {
            return 6;
        }
        if (this.viewStateFilterState() && this.appService.sideBarExpanded()) {
            return 6;
        }
        if (this.viewStateFilterState() || this.appService.sideBarExpanded()) {
            return 9;
        }
        return 12;
    });

    wordCount = computed(() => {
        const parsedStoryText = parseXml(this.data()?.text, 'text/html');
        if (parsedStoryText.body) {
            const bodyChildren = parsedStoryText.body.children;
            let count = 0;
            for (let i = 0; i < bodyChildren.length; i++) {
                const child = bodyChildren[i];
                if (child?.tagName.toUpperCase() !== 'DIV') {
                    count += child?.textContent
                        ?.replace(/,|;|:/g, ' ')
                        ?.replace(/\s\s+/g, ' ')
                        ?.split(/\s+/)
                        ?.map(val => val.trim())
                        ?.filter(val => !!val).length;
                }
            }
            return `${count || 0} ${this.store.selectSignal(selectTranslatedKey(i18nKeys.PSTWebWordsLabel))()}`;
        }
    });

    approvedVisible = computed(() => {
        const fieldApproved = PSTFieldNames.Approved;
        const item = this.store.selectSignal(selectFieldDefsDict)()[fieldApproved];
        return item ? item.Type && (item.Type.indexOf('Script') >= 0 || item.Type.indexOf('RO Column') >= 0 || item.Type.indexOf('RO+NG Column') >= 0 || item.StoryProp) : false;
    });

    get isRTL() {
        return this.globalTablesService.getRtl();
    }

    constructor(
        public appService: AppService,
        public viewStateService: LayoutState
    ) {
        super();
        effect(() => {
            if (this.data()) {
                this.itemStringCache.clear();
            }
        });
    }

    onTopicOpenHandler(data: StorytellingTopicRecord) {
        if (!this.inFullscreenMode()) return;

        this.store.dispatch(
            OpenFullscreenModalActions.openTopicFullscreenDialog({
                recordGuid: data?.Guid,
                recordType: data?.Type,
                isMetadata: true,
            })
        );
    }
}
